import {
	CustomText,
	NewOrderCouldNotGeneratePriceEstimateHeader,
	NewOrderEstimateErrorReUploadBullet,
	NewOrderEstimateErrorSubHeader,
	NewOrderLineItemImportErrorSubheader,
	NewOrderPleaseContactCare,
	NewOrderPleaseSubmitRequestText,
	OrderNumberText,
	SubmitTandemRequestText,
	UnavailableDataPlaceholderText
} from "constants/text";
import { newOrderActions } from "features/reducers/newOrder/newOrder.ts";
import { Dispatch, MutableRefObject, useCallback } from "react";
import { useDispatch } from "react-redux";
import { NewOrderSummaryDetailModel } from "../NewOrdersHeader/new-order-summary-details-model";
import { convertToTitleCase } from "utils/string";
import { ValidateProductsResponseData } from "types/api/products/postValidate";
import { models } from "types/api/viewModels.ts";
import { GetAccountsResponseData } from "types/api/accounts/getAccounts.ts";
import { displayDesigner } from "utils/order";
import { v4 as uuidv4 } from "uuid";
import { ConfigurationFileName } from "components/NewOrders/NewOrders.styles.ts";
import {
	BusinessPurposeAptOrDuplexText,
	BusinessPurposeNewHouseText,
	BusinessPurposeRepairRemodelText,
	BusinessPurposeStockOrderText,
	BusinessPurposeUnknownText,
	NAOptionText
} from "../constants.ts";
import { APIErrorResponse } from "features/api/utils/apiUtils.ts";
import { NewOrderPriceValidationErrorBanners } from "constants/errorBanners.ts";
import Link from "@mui/material/Link";
import OpenInNew from "@mui/icons-material/OpenInNew";
import { UnknownAction } from "@reduxjs/toolkit";
import { components } from "types/declarations/api";
import { OrderBusinessPurpose } from "types/declarations/enums.ts";
import { getOrderTypeDisplayLabel } from "utils/order";

export const useCheckForBillToAccountErrors = (
	onSuccess: () => void,
	csvContents: models["ValidatedOrderViewModel"][] | undefined
) => {
	const dispatch = useDispatch();

	return useCallback(() => {
		let canContinue = true;
		csvContents?.forEach((file, fileIndex) => {
			file?.configurations?.forEach((configuration) => {
				if (!configuration?.accountNumber) {
					dispatch(
						newOrderActions.updateAccountNumber({
							fileIndex: fileIndex,
							productLineCode: configuration.globals?.productLine?.code,
							newAccountNumber: null
						})
					);
					canContinue = false;
				}
				if (!configuration?.billToId && configuration?.accountNumber) {
					dispatch(
						newOrderActions.updateBillToNumber({
							fileIndex: fileIndex,
							productLineCode: configuration.globals?.productLine?.code,
							newBillToNumber: null
						})
					);
					canContinue = false;
				}
			});
		});
		if (canContinue) {
			onSuccess();
		}
	}, [csvContents, dispatch, onSuccess]);
};

export const useCheckForUpdatedItemsBillToAccountErrors = (
	onSuccess: () => void,
	csvContents: models["ValidatedOrderViewModel"] | undefined,
	handleUpdateAccount: (
		account: models["CustomerAccountViewModel"] | null,
		configurationId: string | null | undefined
	) => void,
	handleUpdateBillTo: (
		billTo: models["BillToViewModel"] | null | undefined,
		configurationId: string | null | undefined
	) => void
) => {
	return useCallback(() => {
		let canContinue = true;
		const configurations = csvContents?.configurations;
		if (configurations) {
			for (let i = 0; i < configurations.length; i++) {
				if (!configurations[i]?.accountNumber) {
					handleUpdateAccount(null, configurations[i]?.configurationId);
					canContinue = false;
				}
				if (!configurations[i]?.billToId) {
					handleUpdateBillTo(null, configurations[i]?.configurationId);
					canContinue = false;
				}
				if (!configurations[i]?.billToId && configurations[i]?.accountNumber) {
					handleUpdateBillTo(null, configurations[i]?.configurationId);
					canContinue = false;
				}
			}
		}
		if (canContinue) {
			onSuccess();
		}
	}, [csvContents?.configurations, handleUpdateAccount, handleUpdateBillTo, onSuccess]);
};

export const getNewOrderSummaryDetailInfo = (
	parsedCSV: models["ValidatedOrderViewModel"] | models["DraftOrderViewModel"] | null | undefined,
	validatedOrderResponse: ValidateProductsResponseData | null | undefined
): NewOrderSummaryDetailModel => {
	const configs = validatedOrderResponse?.configurations ?? parsedCSV?.configurations;
	const productLine = new Set(configs?.map((configuration) => configuration.globals?.productLine?.description));

	// Note: Javascript has issues with floating point arithmetic and makes entries like 1.015 multiplied by 100 to 101.49999999...
	// Adding the Number.EPSILON to the cube count before multiplying by 100 addresses this issue
	const cube100x = validatedOrderResponse
		? ((validatedOrderResponse?.counts?.cubeCount ?? 0) + Number.EPSILON) * 100
		: 0;
	const roundedCube = Math.round(cube100x) / 100;
	return {
		productLines: Array.from(productLine).join(", ") || UnavailableDataPlaceholderText,
		accessoryCount: validatedOrderResponse?.counts?.accessoryCount?.toString() ?? UnavailableDataPlaceholderText,
		cabinetCount: validatedOrderResponse?.counts?.cabinetCount?.toString() ?? UnavailableDataPlaceholderText,
		cubeCount: validatedOrderResponse?.counts?.cubeCount
			? roundedCube.toFixed(2)?.toString()
			: UnavailableDataPlaceholderText
	};
};
export const todayDate = new Date();

export const sortGlobalByDescription = (arr: any[]) => {
	return [...arr].sort((a, b) => a.description.localeCompare(b.description));
};

export const formatFinishDescription = (
	finish: models["FinishViewModel"] | models["FinishGlobalsViewModel"] | string | null | undefined,
	isCustomColor?: boolean
): string => {
	const isFinishObject = (value: unknown): value is models["FinishViewModel"] | models["FinishGlobalsViewModel"] =>
		typeof value === "object" && value !== null;
	const isMissingCustomColorInfo =
		isFinishObject(finish) &&
		(!finish.customColor || (!finish.customColor.vendorDescription && !finish.customColor.colorDescription));
	const customColorSampleOrderNumber = isFinishObject(finish) && finish.customColorSampleOrderNumber;
	if (typeof finish === "string" && finish !== "") {
		return finish;
	}

	if (!finish || (!isFinishObject(finish) && finish === "") || (isFinishObject(finish) && finish === null)) {
		return UnavailableDataPlaceholderText;
	}

	if (isCustomColor || customColorSampleOrderNumber) {
		const parts = [
			finish.customColor?.vendorDescription,
			finish.customColor?.colorDescription,
			finish.customColorSampleOrderNumber &&
				isMissingCustomColorInfo &&
				`${OrderNumberText}${finish.customColorSampleOrderNumber}`,
			isMissingCustomColorInfo && !finish?.customColorSampleOrderNumber && UnavailableDataPlaceholderText
		];

		return parts.filter(Boolean).join(" - ");
	}

	return finish.description || UnavailableDataPlaceholderText;
};

export const formatFinishOption = (
	finish: models["FinishViewModel"] | models["FinishGlobalsViewModel"] | null | undefined
): string => {
	const isCustomColor = Boolean(finish?.customColorSampleOrderNumber);
	const isColorCodeCustom = finish?.customColor?.colorCode === CustomText;
	if (isCustomColor) {
		const finishLabel = [
			finish?.customColor?.vendorDescription,
			!isColorCodeCustom && finish?.customColor?.colorCode,
			finish?.customColor?.colorDescription,
			finish?.customColorSampleOrderNumber && `${OrderNumberText}${finish.customColorSampleOrderNumber}`
		];

		return finishLabel.filter(Boolean).join(" - ");
	}

	return finish?.description || UnavailableDataPlaceholderText;
};

export const sortCustomColorFinishesByDescription = (isCustomColor: boolean, arr: any[]) => {
	const customColorValueArray = arr.filter((item) => item.customColorSampleOrderNumber);
	const sortedCustomColorValueArray = [...customColorValueArray].sort((a, b) =>
		a?.customColor?.vendorDescription?.localeCompare(b?.customColor?.vendorDescription)
	);

	const nonCustomColorValueArray = arr.filter((item) => !item.customColorSampleOrderNumber);
	const sortedNonCustomColorValueArray = [...nonCustomColorValueArray].sort((a, b) =>
		a.description.localeCompare(b.description)
	);

	if (isCustomColor) {
		return [...arr].sort((a, b) => a.description.localeCompare(b.description));
	}
	return sortedCustomColorValueArray.length > 0
		? [...sortedNonCustomColorValueArray, { type: "divider" }, ...sortedCustomColorValueArray]
		: [...sortedNonCustomColorValueArray];
};

export const addNAOption = (arr: any[]) => {
	const sortedArr = [...arr].sort((a, b) => a.description.localeCompare(b.description));
	const naOption = { description: "N/A" };
	return [naOption, ...sortedArr];
};

export const sortedAccounts = (accounts: GetAccountsResponseData | null | undefined) => {
	if (accounts) {
		accounts = accounts.slice().sort((a, b) => (Number(a?.accountNumber) > Number(b?.accountNumber) ? 1 : -1));
	}
	return accounts?.filter((account) => account.isActive);
};

export const sortedBillTos = (billTos: models["BillToViewModel"][] | null | undefined) => {
	if (billTos) {
		billTos = billTos.slice().sort((a, b) => {
			const projectNameA = a?.projectName && a?.projectName.trim() !== "" ? a?.projectName + " - " : "";
			const logisticsModeA = a?.logisticsMode ? " - " + convertToTitleCase(a.logisticsMode) : "";

			const projectNameB = b?.projectName && b?.projectName.trim() !== "" ? b?.projectName + " - " : "";
			const logisticsModeB = b?.logisticsMode ? " - " + convertToTitleCase(b.logisticsMode) : "";

			return String(projectNameA + a?.city + a?.state + logisticsModeA + "-" + a.billToId) >
				String(projectNameB + b?.city + b?.state + logisticsModeB + "-" + b.billToId)
				? 1
				: -1;
		});
	}
	return billTos?.filter((billTo) => billTo.isActive);
};

export const getProductLineAccountInfo = (
	config: models["ValidatedOrderConfigurationViewModel"] | models["PendingOrderConfigurationViewModel"] | undefined,
	productLineAccountInfoMap: Record<string, GetAccountsResponseData>
): GetAccountsResponseData | undefined => {
	return config ? productLineAccountInfoMap[config?.globals?.productLine?.code ?? ""] : undefined;
};

export const getConfigurationAccountInfo = (
	config: models["ValidatedOrderConfigurationViewModel"] | models["PendingOrderConfigurationViewModel"] | undefined,
	productLineAccountInfoMap: Record<string, GetAccountsResponseData>
): models["CustomerAccountViewModel"] | undefined => {
	return config
		? getProductLineAccountInfo(config, productLineAccountInfoMap)?.find(
				(accountInfo) => accountInfo.accountNumber === config.accountNumber
			)
		: undefined;
};

export const calculateIntervals = (min: number | undefined, max: number | undefined, increment: number | undefined) => {
	if (typeof min !== "undefined" && typeof max !== "undefined" && typeof increment !== "undefined") {
		let arr = [];
		for (let i = min; i <= max; i += increment) {
			arr.push(i);
		}
		if (arr.indexOf(max) === -1) arr.push(max);

		return arr.slice().map((val) => String(val));
	}
	return [];
};

export const sortDesigners = (designers: models["DesignerViewModel"][]): models["DesignerViewModel"][] => {
	return designers.sort((a, b) => {
		const aDisplay = displayDesigner(a) ?? "";
		const bDisplay = displayDesigner(b) ?? "";

		const letterOrder = /^[A-Za-z]/;
		const aIsLetter = letterOrder.test(aDisplay?.[0] ?? "");
		const bIsLetter = letterOrder.test(bDisplay?.[0] ?? "");

		if (aIsLetter && !bIsLetter) return -1;
		if (!aIsLetter && bIsLetter) return 1;
		return aDisplay?.localeCompare(bDisplay);
	});
};

interface GroupedImportOrderResponseByProductLine {
	[key: string]: models["ValidatedOrderViewModel"][];
}

// Groups configurations and combines door styles and finishes for display in the import modal
export const getGroupedConfigurations = (
	importedOrders: models["ValidatedOrderViewModel"][] | null | undefined
): GroupedImportOrderResponseByProductLine =>
	importedOrders?.reduce((accum: GroupedImportOrderResponseByProductLine, current) => {
		current?.configurations?.forEach((config) => {
			const productLineCode = config.globals?.productLine?.code;
			if (productLineCode && !accum[productLineCode]) {
				accum[productLineCode] ??= [{ ...current, configurations: [config] }];
			} else if (productLineCode && accum[productLineCode]) {
				const matchingFileIndex = accum[productLineCode].findIndex(
					(order) => order?.fileName === current.fileName
				);

				if (~matchingFileIndex) {
					accum[productLineCode][matchingFileIndex]?.configurations?.push(config);
				} else {
					accum[productLineCode].push({ ...current, configurations: [config] });
				}
			}
		});

		return accum;
	}, {}) ?? {};

export interface ConfigurationGroup {
	accountId: string | null | undefined;
	accountName: string | null | undefined;
	accountNumber: string | null | undefined;
	billToId: string | null | undefined;
	configurations: (models["PendingOrderConfigurationViewModel"] | models["ValidatedOrderConfigurationViewModel"])[];
	globals: models["GlobalsViewModel"] | undefined;
	id: string;

	[key: symbol]: string;
}
// Groups configurations for line item grid display
export const getConfigurationGroupsByProductLine = (
	configurations:
		| (models["PendingOrderConfigurationViewModel"] | models["ValidatedOrderConfigurationViewModel"])[]
		| null
		| undefined,
	existingGroups?: ConfigurationGroup[]
) => {
	if (!configurations) return;
	const productLineGroups = configurations.reduce((accum: ConfigurationGroup[], current) => {
		if (
			!accum.find((config) => config.globals?.productLine?.code === current.globals?.productLine?.code) &&
			current.globals?.productLine
		) {
			const existingGroupId = existingGroups?.find(
				(group) => group.globals?.productLine?.code === current.globals?.productLine?.code
			)?.id;
			accum.push({
				accountId: undefined,
				accountName: undefined,
				accountNumber: undefined,
				billToId: undefined,
				globals: current.globals,
				configurations: [],
				id: existingGroupId ?? uuidv4()
			});
		}
		return accum;
	}, []);
	configurations.forEach((config) => {
		const groupIndex = productLineGroups.findIndex(
			(group) => group.globals?.productLine?.code === config.globals?.productLine?.code
		);
		if (~groupIndex && productLineGroups[groupIndex]) {
			productLineGroups[groupIndex].configurations.push(config);
			productLineGroups[groupIndex].accountId ??= config.accountId;
			productLineGroups[groupIndex].accountName ??= config.accountName;
			productLineGroups[groupIndex].accountNumber ??= config.accountNumber;
			productLineGroups[groupIndex].billToId ??= config.billToId;
			productLineGroups[groupIndex].globals ??= config.globals;
		}
	});

	return productLineGroups;
};

export const shuffleLineItems = (
	configurations: models["PendingOrderConfigurationViewModel"][],
	selectedRemovedItem: MutableRefObject<models["PendingLineItemViewModel"] | undefined>
): models["PendingOrderConfigurationViewModel"][] => {
	let lineItemNumber = 1;
	return configurations.map((config) => ({
		...config,
		lineItems: config?.lineItems
			?.filter((configItem) => configItem.lineItemNumber !== selectedRemovedItem?.current?.lineItemNumber)
			.map((item) => {
				const newModifications: models["PendingModificationViewModel"][] =
					item.modifications?.map((modification, index) => ({
						...modification,
						lineItemNumber: Number(`${lineItemNumber}.${index + 1}`)
					})) ?? [];
				const newLineItem: models["PendingLineItemViewModel"] = {
					...item,
					lineItemNumber: lineItemNumber,
					modifications: newModifications ?? null
				};
				lineItemNumber += 1;
				return newLineItem;
			})
	}));
};

export const getAttributes = (
	configuration: models["PendingOrderConfigurationViewModel"] | null | undefined,
	isCustomColor?: boolean
) => {
	const customColorVendor = configuration?.globals?.finish?.customColor?.vendorDescription;
	const customColorDescription = configuration?.globals?.finish?.customColor?.colorDescription;
	return [
		configuration?.globals?.productLine?.description,
		configuration?.globals?.style?.description,
		configuration?.globals?.species?.description,
		configuration?.globals?.finish?.customColor
			? `${customColorVendor} - ${customColorDescription}`
			: configuration?.globals?.finish?.description
	]
		.filter(Boolean)
		.join(", ");
};

export const getSubheaderAttributes = (configuration: models["PendingOrderConfigurationViewModel"]) => {
	return [
		configuration?.globals?.shape?.description,
		configuration?.globals?.profile?.description,
		configuration?.globals?.hingeType?.description,
		configuration?.globals?.case?.description,
		configuration?.globals?.distressing?.description !== NAOptionText &&
			configuration?.globals?.distressing?.description,
		configuration?.globals?.accentColor?.description !== NAOptionText &&
			configuration?.globals?.accentColor?.description,
		configuration?.globals?.accentApplication?.description !== NAOptionText &&
			configuration?.globals?.accentApplication?.description,
		configuration?.globals?.construction?.description,
		configuration?.globals?.packaging?.description
	]
		.filter(Boolean)
		.join(", ");
};

export const getConfigFileName = (
	configuration: models["ValidatedOrderConfigurationViewModel"],
	csvData: (models["ValidatedOrderViewModel"] | undefined)[] | undefined
): string | null | undefined => {
	const matchingCSV = csvData?.find((csv) =>
		csv?.configurations?.find((config) => config.configurationId === configuration.configurationId)
	);
	return matchingCSV ? matchingCSV?.fileName : undefined;
};

export const getConfigDisplay = (
	configuration: models["ValidatedOrderConfigurationViewModel"],
	csvData: (models["ValidatedOrderViewModel"] | undefined)[] | undefined
) => {
	const fileName = getConfigFileName(configuration, csvData);
	return fileName ? <ConfigurationFileName>{fileName}</ConfigurationFileName> : <></>;
};

export const getLineItemCount = (group: ConfigurationGroup) => {
	const count = group.configurations?.reduce((accum, current) => accum + (current.lineItems?.length ?? 0), 0) ?? 0;
	return `${count} line item${count > 1 ? "s" : ""}`;
};

export const runForAllConfigs = (
	csvContents: models["ValidatedOrderViewModel"][] | undefined,
	importedCSVs: models["ValidatedOrderViewModel"][] | undefined,
	callback: (
		reduxConfigIndex: number,
		configIndex: number,
		config: models["PendingOrderConfigurationViewModel"]
	) => void
) => {
	csvContents?.forEach((csv) =>
		csv?.configurations?.forEach((config) => {
			const reduxCSVIndex =
				importedCSVs?.findIndex((importedCSV) => importedCSV?.fileName === csv.fileName) ?? -1;
			const reduxConfigIndex =
				importedCSVs?.[reduxCSVIndex]?.configurations?.findIndex(
					(csvConfig) => csvConfig.configurationId === config.configurationId
				) ?? -1;
			callback(reduxCSVIndex, reduxConfigIndex, config);
		})
	);
};

export const findAccount = (accounts: GetAccountsResponseData | undefined, accountNumber: string | null | undefined) =>
	accounts?.find((account) => account?.accountNumber === accountNumber);

export const findBillTo = (
	selectedAccount: models["CustomerAccountViewModel"] | null | undefined,
	billToId: string | null | undefined
) => selectedAccount?.billTos?.find((billTo) => billTo.billToId === billToId);

export const formatOrderType = (orderType: models["OrderType"] | undefined) => {
	let formattedOrderType = getOrderTypeDisplayLabel(orderType);
	return `${formattedOrderType} Order`;
};

export const isDimensionValid = (dimension: string | null | undefined) => Boolean(Number(dimension));

export const containsCustomQuoteSku = (order: models["ValidatedOrderViewModel"] | null | undefined) => {
	const lineItems = order?.configurations?.flatMap((config) =>
		config?.lineItems?.flatMap((lineItem) => lineItem.requiredDimensions)
	);
	return lineItems?.some((dimension) => dimension?.isUnrestrictedValue);
};

export const handlePriceValidationErrorMessaging = (
	error: string[],
	apiError: APIErrorResponse | undefined,
	amountOfLineItemErrors?: number,
	isCustomQuote?: boolean
) => {
	const customQuoteErrorBanners = NewOrderPriceValidationErrorBanners.map((banner) => {
		return {
			...banner,
			bannerBullets: banner.bannerBullets.filter((text) => text !== NewOrderEstimateErrorReUploadBullet)
		};
	});
	const foundError = (isCustomQuote ? customQuoteErrorBanners : NewOrderPriceValidationErrorBanners).find((banner) =>
		error.includes(banner.bannerType)
	);
	const bulletPoints =
		error.includes("NewOrder400ErrorBanner") && apiError?.errors
			? apiError?.errors.map((response) => response.message)
			: foundError?.bannerBullets;
	let header;
	let subheader;
	if (foundError) {
		header = foundError.bannerHeader;
		subheader = foundError.bannerSubheader;
	} else if (error.includes("NewOrderLineItemImportErrorBanner") && amountOfLineItemErrors) {
		header = `We found ${amountOfLineItemErrors} line item error${amountOfLineItemErrors > 1 ? "s" : ""} in your order`;
		subheader = NewOrderLineItemImportErrorSubheader;
	} else {
		header = NewOrderCouldNotGeneratePriceEstimateHeader;
		subheader = NewOrderEstimateErrorSubHeader;
	}
	return (
		<div>
			<div>{header}</div>
			<div>{subheader}</div>
			{bulletPoints && bulletPoints.length > 0 && (
				<ul>
					{bulletPoints.map((bulletPoint) => (
						<li key={bulletPoint}>{bulletPoint}</li>
					))}
				</ul>
			)}
			{error.includes("NewOrder502ErrorBanner") ? (
				<div>
					{NewOrderPleaseSubmitRequestText}&nbsp;
					<Link
						href="https://tandem.cabinetworksgroup.com/s/tandem-support"
						target="_blank"
						rel="noreferrer"
					>
						{SubmitTandemRequestText}
					</Link>
					.
					<Link>
						<OpenInNew />
					</Link>
				</div>
			) : (
				<>
					{!error.includes("NewOrder40001ErrorBanner") &&
						!error.includes("NewOrderLineItemImportErrorBanner") && <div>{NewOrderPleaseContactCare}</div>}
					{apiError && <div>({error.includes("NewOrder40001ErrorBanner") ? "40001" : apiError?.status})</div>}
				</>
			)}
		</div>
	);
};

// Import CSV utils:
export const fetchProductLineAccounts = async (
	importedCSVData: models["ValidatedOrderViewModel"],
	dispatch: Dispatch<UnknownAction>,
	getAccountByProductLine: any
) => {
	const fetchedProductLineAccounts: string[] = [];
	for (let configuration of importedCSVData?.configurations ?? []) {
		const productLineCode = configuration?.globals?.productLine?.code;
		if (productLineCode && !fetchedProductLineAccounts.includes(productLineCode)) {
			const accountInfo = await getAccountByProductLine(productLineCode).unwrap();
			dispatch(newOrderActions.addProductLineAccountInfo([productLineCode, accountInfo]));
			fetchedProductLineAccounts.push(productLineCode);
		}
	}
};

export const doDispatches = (
	importedCSVData: models["ValidatedOrderViewModel"] | undefined,
	dispatch: Dispatch<UnknownAction>,
	isNewOrderBuildOrderPage: boolean,
	isAddOnBuildOrderPage: boolean,
	draftOrder?: models["DraftOrderViewModel"]
) => {
	if (!importedCSVData) return;

	if (Array.isArray(importedCSVData.configurations)) {
		importedCSVData.configurations = importedCSVData.configurations.map((config) => ({
			...config,
			configurationId: uuidv4()
		}));
	}
	if ((isNewOrderBuildOrderPage || isAddOnBuildOrderPage) && importedCSVData) {
		dispatch(
			newOrderActions.addTempParsedCSV({
				importedCSV: importedCSVData,
				existingConfigs: draftOrder?.configurations ?? []
			})
		);
	}

	if (!isNewOrderBuildOrderPage && !isAddOnBuildOrderPage && importedCSVData) {
		dispatch(newOrderActions.addParsedCSV(importedCSVData));
	}
};

export const businessPurposeEnumDescription = (purpose: components["schemas"]["OrderBusinessPurpose"] | undefined) => {
	switch (purpose) {
		case OrderBusinessPurpose.REPAIR_REMODEL:
			return BusinessPurposeRepairRemodelText;
		case OrderBusinessPurpose.NEW_CONSTRUCTION_SF:
			return BusinessPurposeNewHouseText;
		case OrderBusinessPurpose.MULTI_FAMILY_APARTMENTS:
			return BusinessPurposeAptOrDuplexText;
		case OrderBusinessPurpose.STOCK_ORDER:
			return BusinessPurposeStockOrderText;
		case OrderBusinessPurpose.UNKNOWN:
			return BusinessPurposeUnknownText;
		default:
			return null;
	}
};
